import { useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState, type DragEvent } from "react";

import { IMAGE_TYPES, pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { useImageUpload, type ImageUpload } from "../../../platform/media/image-upload";
import { maximumFiles } from "../../../ui/format";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";
/** Live's words, its types in its order. */
const UNSUPPORTED = "Some files have unsupported file types image/png,image/jpeg,image/webp";

/** Where the files go: the simple composer's references, or the Advanced mode's (#234). */
export interface DropTarget {
  addReference: (image: PickedImage) => void;
  /** How many more references fit. */
  room: number;
  /** Whether the composer takes a reference now (not while a start runs, nor when hidden). */
  canTake: boolean;
  /**
   * Asks before the files upload, when the composer must (the Advanced mode's "Use Style or
   * Image?"): `go` uploads them; never calling it drops them.
   */
  ask?: ((go: () => void) => void) | undefined;
}

export interface ReferenceDrop {
  /** Files are over the composer. */
  over: boolean;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  /** The upload under way, its progress and its refusal. */
  upload: ImageUpload;
  /** Live's word when the files do not fit or are not images, until the next drop. */
  problem: string | null;
}

/**
 * Live's drop and paste on the image composer (#233, LEFTOVERS 6): files dropped on the composer
 * or pasted anywhere on the page become references, one after another through the library's one
 * upload, up to the room the cap leaves. Live's words when they do not fit: "Maximum n files
 * allowed"; when some are not images it takes: "Some files have unsupported file types ...".
 */
export function useReferenceDrop(target: DropTarget): ReferenceDrop {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useImageUpload({
    teamId,
    brandId,
    onUploaded: (asset) =>
      target.addReference({ assetId: asset.id, url: pictureOf(asset), name: asset.name }),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const { room, canTake } = target;
  const take = async (files: File[]) => {
    if (files.length === 0) return;
    // Live's `validateFiles`: the count first, then the types.
    if (files.length > room) {
      setProblem(maximumFiles(room));
      return;
    }
    const taken: readonly string[] = IMAGE_TYPES;
    const fit = files.filter((file) => taken.includes(file.type));
    if (fit.length < files.length) {
      setProblem(UNSUPPORTED);
      return;
    }
    setProblem(null);
    const send = async () => {
      for (const file of fit) await upload.send(file);
    };
    if (target.ask === undefined) await send();
    else target.ask(() => void send());
  };
  // Live's paste: anywhere on the page, files only, while the composer can take a reference.
  const pasteRef = useRef(take);
  useEffect(() => {
    pasteRef.current = take;
  });
  useEffect(() => {
    if (!canTake) return undefined;
    const onPaste = (event: ClipboardEvent) => {
      const files = Array.from(event.clipboardData?.files ?? []);
      if (files.length === 0) return;
      event.preventDefault();
      void pasteRef.current(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [canTake]);
  return {
    over,
    onDragOver: (event) => {
      if (!canTake || !event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      setOver(true);
    },
    onDragLeave: (event) => {
      // Leaving for a child of the composer is not leaving it.
      if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
      setOver(false);
    },
    onDrop: (event) => {
      if (!canTake) return;
      event.preventDefault();
      setOver(false);
      void take(Array.from(event.dataTransfer.files));
    },
    upload,
    problem,
  };
}
